import React, { useRef, useEffect } from 'react';
import { useDragLayer } from 'react-dnd';

const DEFAULT_SCROLL_EDGE_SIZE = 120;
const SCROLL_SPEED = 8;

function getScrollContainer(tableEl: HTMLDivElement | null): HTMLElement | null {
  if (!tableEl) return null;
  let el: HTMLElement | null = tableEl;
  while (el) {
    const { overflowY } = getComputedStyle(el);
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      if (el.scrollHeight > el.clientHeight) return el;
    }
    el = el.parentElement;
  }
  return document.documentElement;
}

interface DragScrollControllerProps {
  tableRef: React.RefObject<HTMLDivElement | null>;
  dragType: string;
  scrollEdgeSize?: number;
}

export function DragScrollController({
  tableRef,
  dragType,
  scrollEdgeSize = DEFAULT_SCROLL_EDGE_SIZE,
}: DragScrollControllerProps) {
  const offsetRef = useRef<{ x: number; y: number } | null>(null);
  const rafIdRef = useRef<number | null>(null);

  const { isDragging, currentOffset } = useDragLayer((monitor) => {
    if (monitor.getItemType() !== dragType) {
      return { isDragging: false, currentOffset: null };
    }
    return {
      isDragging: monitor.isDragging(),
      currentOffset: monitor.getClientOffset(),
    };
  });

  if (currentOffset) {
    offsetRef.current = currentOffset;
  }

  useEffect(() => {
    if (!isDragging) {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const tick = () => {
      if (cancelled) return;

      const container = getScrollContainer(tableRef.current);
      const offset = offsetRef.current;
      if (!container || !offset) {
        rafIdRef.current = requestAnimationFrame(tick);
        return;
      }

      const isWindow = container === document.documentElement;
      const top = isWindow ? 0 : container.getBoundingClientRect().top;
      const bottom = isWindow ? window.innerHeight : container.getBoundingClientRect().bottom;
      const cursorY = offset.y;

      if (cursorY < top + scrollEdgeSize) {
        const diff = (top + scrollEdgeSize - cursorY) / scrollEdgeSize;
        container.scrollTop -= SCROLL_SPEED * Math.min(1, diff);
      } else if (cursorY > bottom - scrollEdgeSize) {
        const diff = (cursorY - (bottom - scrollEdgeSize)) / scrollEdgeSize;
        container.scrollTop += SCROLL_SPEED * Math.min(1, diff);
      }

      rafIdRef.current = requestAnimationFrame(tick);
    };

    rafIdRef.current = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [isDragging, tableRef, scrollEdgeSize]);

  return null;
}
